import React, { useCallback, useEffect, useState } from 'react'
import { DatePicker } from '@mui/x-date-pickers'
import { format as formatDate, isValid, parseISO } from 'date-fns'
import {
  Box, Button, Chip, FormControl, InputLabel, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Tooltip,
  Typography
} from '@mui/material'
import { ReplayRounded } from '@mui/icons-material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { entityTypeLabelKey } from '../../generic/qr/qrEntityTypes'
import { listPrinters, listPrintHistory, relaunchPrint } from '../../generic/qr/labelPrinting/labelPrintingApi'
import { OUTCOME_TEXT, PROBLEM_TEXT, localDateTimeText, textOf } from '../../generic/qr/labelPrinting/labelPrintingTexts'
import useLabelPrintFlow from '../../generic/qr/labelPrinting/useLabelPrintFlow'
import LabelPrintFeedback from '../../generic/qr/labelPrinting/LabelPrintFeedback'
import UserAutocomplete from '../../generic/qr/labelPrinting/UserAutocomplete'

const PAGE_SIZE = 25
// E15: every filter is optional and they combine (AND); '' means "no filter". Dates travel as yyyy-MM-dd.
const NO_FILTERS = { from: '', to: '', username: '', printerId: '', outcome: '' }
const RESULT_FILTERS = [{ value: 'PRINTED', text: 'Printed' }, { value: 'ERROR', text: 'With errors' }]
const DATE_FORMAT = 'yyyy-MM-dd'

const COLUMNS = ['Date', 'User', 'Element', 'Template version', 'Copies', 'Printer', 'Result', 'Reason']

// Sun sends the wire form (PART, RAWMATERIAL…; tolerate RAW_MATERIAL too) and the label key is shared with MOS.
// E12: a test print (entityType TEST, entityId 0) has no element: it reads just "Test" in the user's language.
const TEST_TYPE = 'TEST'
const isTestRecord = ({ entityType }) => entityType === TEST_TYPE
const elementText = (t_, record) => (isTestRecord(record) ? t_(TEST_TYPE) : elementOf(t_, record))
const elementOf = (t_, { entityType, entityId }) => `${t_(entityTypeLabelKey(String(entityType).replace(/_/g, '')))} ${entityId}`

const ResultChip = ({ record }) => {
  const { t_ } = useSmartTranslation()
  const printed = record.outcome === 'PRINTED'
  const chip = (
    <Chip size='small' color={printed ? 'success' : 'error'} label={t_(textOf(OUTCOME_TEXT, record.outcome))} />
  )
  const problems = record.problems || []
  if (problems.length === 0) return chip
  return (
    <Tooltip title={<Box>{problems.map((problem) => <div key={problem}>{t_(textOf(PROBLEM_TEXT, problem))}</div>)}</Box>}>
      <span>{chip}</span>
    </Tooltip>
  )
}

// Historia 226 (D5, RN38, RN39; S145-S151, CU7-CU8): every print attempt, newest first, paginated in Sun (no filters,
// DL8). Loaded when the tab is entered and when the page changes or a relaunch ends: never by itself.
const PrintHistory = () => {
  const { t_ } = useSmartTranslation()
  const [records, setRecords] = useState([])
  const [total, setTotal] = useState(0)
  const [pageNumber, setPageNumber] = useState(0)
  const [loadFailed, setLoadFailed] = useState('') // text key of the failure, '' when the last load worked
  const [filters, setFilters] = useState(NO_FILTERS)
  const [printers, setPrinters] = useState([])

  const load = useCallback(async (requestedPage, requestedFilters) => {
    try {
      const result = await listPrintHistory(requestedPage, PAGE_SIZE, requestedFilters)
      setRecords(result.content || [])
      setTotal(result.totalElements || 0)
      setPageNumber(requestedPage)
      setLoadFailed('')
    } catch (error) {
      setRecords([])
      setTotal(0)
      // E15: Sun answers a 400 INVALID_REQUEST with `field: 'from'` when the start date is after the end date.
      setLoadFailed(error?.data?.outcome === 'INVALID_REQUEST' && error.data.field === 'from'
        ? 'The start date must not be after the end date'
        : 'Unexpected error')
    }
  }, [])

  // Changing a filter goes back to the first page.
  useEffect(() => { load(0, filters) }, [load, filters])

  useEffect(() => {
    listPrinters().then((all) => setPrinters(Array.isArray(all) ? all : [])).catch(() => setPrinters([]))
  }, [])

  const changeFilter = (name, value) => setFilters((current) => ({ ...current, [name]: value }))
  // A half-typed date is not a date yet: the filter only follows a complete one, or a cleared field.
  const changeDate = (name) => (date) => {
    if (date === null) changeFilter(name, '')
    else if (isValid(date)) changeFilter(name, formatDate(date, DATE_FORMAT))
  }
  const dateOf = (text) => (text ? parseISO(text) : null)
  const handleFromChange = changeDate('from')
  const handleToChange = changeDate('to')
  const handleUserChange = (username) => changeFilter('username', username)
  const handlePrinterChange = (event) => changeFilter('printerId', event.target.value)
  const handleOutcomeChange = (event) => changeFilter('outcome', event.target.value)
  const handleClearFilters = () => setFilters(NO_FILTERS)

  // A relaunch is a new attempt (RN38): once it ends, the first page shows it on top.
  const flow = useLabelPrintFlow({ onSettled: () => load(0, filters) })

  // From the history the relaunch is asked without the truncation flag: Sun asks back if the text does not fit (S151).
  const handlePageChange = (event, nextPage) => load(nextPage, filters)

  const relaunch = (record) => flow.attempt((accept) => relaunchPrint(record.id, accept), record.printerName || '')

  return (
    <Box>
      {/* E24: the print notice at the top, like the rest of MIC */}
      <LabelPrintFeedback {...flow} />
      {/* E15: filter bar */}
      <Box data-testid='history-filters' sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
        <DatePicker
          label={t_('From')}
          value={dateOf(filters.from)}
          onChange={handleFromChange}
          format='dd/MM/yyyy'
          slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
        />
        <DatePicker
          label={t_('To')}
          value={dateOf(filters.to)}
          onChange={handleToChange}
          format='dd/MM/yyyy'
          slotProps={{ textField: { size: 'small', sx: { width: 160 } } }}
        />
        <Box sx={{ width: 220 }}>
          <UserAutocomplete value={filters.username} onChange={handleUserChange} label={t_('User')} />
        </Box>
        <FormControl size='small' sx={{ minWidth: 180 }}>
          <InputLabel id='history-filter-printer'>{t_('Printer')}</InputLabel>
          <Select labelId='history-filter-printer' label={t_('Printer')} value={filters.printerId} onChange={handlePrinterChange}>
            <MenuItem value=''>{t_('All')}</MenuItem>
            {printers.map((printer) => <MenuItem key={printer.id} value={String(printer.id)}>{printer.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size='small' sx={{ minWidth: 150 }}>
          <InputLabel id='history-filter-result'>{t_('Result')}</InputLabel>
          <Select labelId='history-filter-result' label={t_('Result')} value={filters.outcome} onChange={handleOutcomeChange}>
            <MenuItem value=''>{t_('All')}</MenuItem>
            {RESULT_FILTERS.map(({ value, text }) => <MenuItem key={value} value={value}>{t_(text)}</MenuItem>)}
          </Select>
        </FormControl>
        <Button onClick={handleClearFilters} disabled={Object.values(filters).every((value) => value === '')}>{t_('Clear filters')}</Button>
      </Box>
      <TableContainer>
        <Table size='small'>
          <TableHead>
            <TableRow>
              {COLUMNS.map((column) => <TableCell key={column}>{t_(column)}</TableCell>)}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {records.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length + 1} align='center'>{t_(loadFailed || 'No label has been printed yet.')}</TableCell>
              </TableRow>
            )}
            {records.map((record) => (
              <TableRow key={record.id} data-testid={`print-record-${record.id}`}>
                <TableCell>{localDateTimeText(record.attemptedAt)}</TableCell>
                <TableCell>{record.username || ''}</TableCell>
                <TableCell>{elementText(t_, record)}</TableCell>
                <TableCell>{record.templateVersion ?? ''}</TableCell>
                <TableCell>{record.copies ?? 1}</TableCell>
                <TableCell>
                  {record.printerAddress
                    ? <Tooltip title={record.printerAddress}><span>{record.printerName || ''}</span></Tooltip>
                    : record.printerName || ''}
                </TableCell>
                <TableCell>
                  <ResultChip record={record} />
                  {record.relaunchedFrom && (
                    <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
                      {t_('Relaunched from #{{id}}', { id: record.relaunchedFrom })}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{record.reason || ''}</TableCell>
                <TableCell>
                  {/* Sun refuses to relaunch a test print (400): it is not an element */}
                  {!isTestRecord(record) && (
                    <Button size='small' startIcon={<ReplayRounded />} onClick={() => relaunch(record)} disabled={flow.busy}>
                      {t_('Relaunch')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component='div'
        count={total}
        page={pageNumber}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[PAGE_SIZE]}
        onPageChange={handlePageChange}
      />
    </Box>
  )
}

export default PrintHistory
